import React from 'react'
import {
  IconButton,
  Box,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
} from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import { getLocale } from 'app/translations/intl-context'
import moment from 'moment'

/**
 * Buddhist calendar-supported custom range calendar toolbar.
 * Uses the exposed calendar API to manage calendar slider and month/year selection
 * without interfering with the selected date value.
 */

const useStyles = makeStyles({
  formControl: {
    minWidth: 100,
    marginLeft: 4,
    marginRight: 2,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  select: {
    '& .MuiSelect-iconOutlined': {
      right: 0,
    },
  },
  menuPaper: {
    maxHeight: 420,
  },
})

const CustomNavigationBar = ({ focusedDate, changeShownDate, props }) => {
  const classes = useStyles()
  const getBuddhistYear = (date) => date.getFullYear() + 543
  const getGregorianYear = (buddhistYear) => buddhistYear - 543

  const isThaiLocale = getLocale() === 'th'

  // Use locale-aware moment.js to get month names
  const monthNames = moment.months()

  const handlePrevious = () => {
    const newDate = new Date(focusedDate)
    newDate.setMonth(newDate.getMonth() - 1)
    changeShownDate(newDate)
  }

  const handleNext = () => {
    const newDate = new Date(focusedDate)
    newDate.setMonth(newDate.getMonth() + 1)
    changeShownDate(newDate)
  }

  const handleMonthChange = (event) => {
    const monthIndex = parseInt(event.target.value, 10)
    const newDate = new Date(focusedDate)
    newDate.setMonth(monthIndex)
    changeShownDate(newDate)
  }

  // disable future months if the focused year is the current year
  const isMonthDisabled = (monthIndex) => {
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth()
    const focusedYear = focusedDate.getFullYear()

    if (focusedYear === currentYear) {
      return monthIndex > currentMonth
    }

    if (focusedYear > currentYear) {
      return true
    }

    return false
  }

  // If Thai locale, convert Buddhist year back to to Gregorian since the value is in Buddhist year
  const handleYearChange = (event) => {
    const yearValue = parseInt(event.target.value, 10)
    const newDate = new Date(focusedDate)

    if (isThaiLocale) {
      const gregorianYear = getGregorianYear(yearValue)
      newDate.setFullYear(gregorianYear)
    } else {
      newDate.setFullYear(yearValue)
    }

    changeShownDate(newDate)
  }

  // generate year options based on locale
  const generateYearOptions = () => {
    const years = []
    const today = new Date()
    const currentYear = today.getFullYear()

    if (isThaiLocale) {
      const currentBuddhistYear = getBuddhistYear(today)
      for (let i = currentBuddhistYear - 100; i <= currentBuddhistYear; i++) {
        years.push(i)
      }
    } else {
      for (let i = currentYear - 100; i <= currentYear; i++) {
        years.push(i)
      }
    }

    return years
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1}
    >
      <IconButton size="small" onClick={handlePrevious}>
        <ChevronLeft />
      </IconButton>

      <Box display="flex" alignItems="center">
        <FormControl
          variant="outlined"
          size="small"
          className={classes.formControl}
        >
          <Select
            value={focusedDate.getMonth()}
            onChange={handleMonthChange}
            className={classes.select}
            MenuProps={{
              classes: {
                paper: classes.menuPaper,
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {monthNames.map((month, index) => (
              <MenuItem
                key={index}
                value={index}
                disabled={isMonthDisabled(index)}
              >
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.formControl}
        >
          <Select
            value={
              isThaiLocale
                ? getBuddhistYear(focusedDate)
                : focusedDate.getFullYear()
            }
            onChange={handleYearChange}
            className={classes.select}
            MenuProps={{
              classes: {
                paper: classes.menuPaper,
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {generateYearOptions().map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <IconButton size="small" onClick={handleNext}>
        <ChevronRight />
      </IconButton>
    </Box>
  )
}

// renderer function needed for range calendar compatibility
const customNavigationRenderer = (focusedDate, changeShownDate, props) => {
  return (
    <CustomNavigationBar
      focusedDate={focusedDate}
      changeShownDate={changeShownDate}
      props={props}
    />
  )
}

export default customNavigationRenderer
